import React, { useState } from "react";
import {
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Stack,
  CircularProgress,
  Alert,
  Box,
  TextField,
  InputAdornment,
  Chip,
  Fade,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/styles";
import SearchIcon from "@material-ui/icons/Search";
import AddIcon from "@material-ui/icons/Add";
import DescriptionIcon from "@material-ui/icons/Description";
import TodayIcon from "@material-ui/icons/Today";
import ClearIcon from "@material-ui/icons/Clear";
import AdapterDateFns from "@material-ui/lab/AdapterDateFns";
import { es } from "date-fns/locale";

import { usePortafolio } from "./hooks/usePortafolio";
import AgregarPortafolio from "./dialogs/AgregarPortafolio";
import { DatePicker, LocalizationProvider } from "@material-ui/lab";
import { format, isValid } from "date-fns";
import { PortafolioCardList } from "./components";

const useStyles = makeStyles((theme) => ({
  content: {
    padding: theme.spacing(3),
    backgroundColor: theme.palette.background.default,
  },
  searchAndFilters: {
    marginBottom: theme.spacing(3),
    backgroundColor: "white",
    borderRadius: 8,
    padding: theme.spacing(2),
    boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
    border: `1px solid ${theme.palette.divider}`,
  },
  addButton: {
    borderRadius: 6,
    textTransform: "none",
    fontWeight: 500,
  },
  searchField: {
    "& .MuiOutlinedInput-root": {
      borderRadius: 6,
      "&:hover fieldset": {
        borderColor: theme.palette.primary.main,
      },
    },
  },
  dateField: {
    marginTop: theme.spacing(3),
  },
  loadingContainer: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    minHeight: 200,
    gap: theme.spacing(2),
  },
  errorContainer: {
    margin: theme.spacing(2, 0),
  },
  statsContainer: {
    display: "flex",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
    flexWrap: "wrap",
  },
  statChip: {
    backgroundColor: theme.palette.grey[100],
    color: theme.palette.text.secondary,
    "& .MuiChip-icon": {
      color: theme.palette.text.secondary,
    },
  },
  emptyState: {
    textAlign: "center",
    padding: theme.spacing(6),
    backgroundColor: "white",
    borderRadius: 8,
    border: `1px solid ${theme.palette.divider}`,
  },
  emptyStateIcon: {
    fontSize: 64,
    color: theme.palette.grey[300],
    marginBottom: theme.spacing(2),
  },
  clearButton: {
    minWidth: "auto",
    padding: theme.spacing(1),
  },
}));

// Función para convertir formato de fecha
const convertirFechaParaFiltro = (fechaPortafolio) => {
  if (!fechaPortafolio) return "";
  const partes = fechaPortafolio.split("/");
  if (partes.length >= 2) {
    return `${partes[0]}-${partes[1]}`;
  }
  return "";
};

export default function Portafolio({ value, cliente }) {
  const classes = useStyles();
  const [agregarPortafolio, setAgregarPortafolio] = useState(false);
  const [portafolioSeleccionado, setPortafolioSeleccionado] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterDate, setFilterDate] = useState(null);

  const {
    portafolios,
    loading,
    error,
    deleteFile,
    deletingFile,
    deletePortafolio,
    deletingPortafolio,
    rol,
  } = usePortafolio(cliente?.rut);

  // Filtrar portafolios
  const filteredPortafolios = portafolios.filter((portafolio) => {
    const matchesSearch = portafolio.descripcion
      .toLowerCase()
      .includes(searchTerm.toLowerCase());

    // Corregir el filtro de fecha
    const fechaPortafolioFormateada = convertirFechaParaFiltro(
      portafolio.fecha,
    );
    const matchesDate =
      !filterDate ||
      !isValid(filterDate) ||
      fechaPortafolioFormateada === format(filterDate, "yyyy-MM");
    return matchesSearch && matchesDate;
  });

  const handleAgregarPortafolio = () => {
    setPortafolioSeleccionado(null);
    setAgregarPortafolio(true);
  };

  const handleEditarPortafolio = (portafolio) => {
    setPortafolioSeleccionado(portafolio);
    setAgregarPortafolio(true);
  };

  const handleCloseModal = () => {
    setAgregarPortafolio(false);
    setPortafolioSeleccionado(null);
  };

  const handleClearFilters = () => {
    setSearchTerm("");
    setFilterDate("");
  };

  // No renderizar si no es la pestaña correcta
  if (value !== 5) {
    return null;
  }

  return (
    <Fade in={true} timeout={300}>
      <div style={{ marginBottom: 12 }}>
        <Card variant="outlined">
          <CardContent className={classes.content}>
            {/* Barra de búsqueda y filtros */}
            <Box className={classes.searchAndFilters}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    fullWidth
                    placeholder="Buscar en descripciones..."
                    variant="outlined"
                    size="small"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className={classes.searchField}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon color="action" />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>

                <Grid item xs={12} sm={4} md={3}>
                  <LocalizationProvider
                    dateAdapter={AdapterDateFns}
                    locale={es}
                  >
                    <DatePicker
                      views={["year", "month"]}
                      label="Filtrar por mes"
                      value={filterDate}
                      onChange={setFilterDate}
                      maxDate={new Date()}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          size="small"
                          fullWidth
                          className={classes.dateField}
                        />
                      )}
                      openTo="month"
                      inputFormat="MM/yyyy"
                    />
                  </LocalizationProvider>
                </Grid>

                <Grid item xs={6} sm={2} md={2}>
                  <Button
                    fullWidth
                    variant="outlined"
                    size="small"
                    onClick={handleClearFilters}
                    startIcon={<ClearIcon />}
                    disabled={!searchTerm && !filterDate}
                  >
                    Limpiar
                  </Button>
                </Grid>

                <Grid item xs={6} sm={12} md={3}>
                  <Button
                    fullWidth
                    variant="contained"
                    color="primary"
                    className={classes.addButton}
                    onClick={handleAgregarPortafolio}
                    disabled={loading}
                    startIcon={<AddIcon />}
                  >
                    Nuevo Portafolio
                  </Button>
                </Grid>
              </Grid>

              {/* Estadísticas */}
              {portafolios.length > 0 && (
                <Box className={classes.statsContainer}>
                  <Chip
                    icon={<DescriptionIcon />}
                    label={`${portafolios.length} registros`}
                    className={classes.statChip}
                    size="small"
                  />
                  <Chip
                    icon={<TodayIcon />}
                    label={`${filteredPortafolios.length} mostrados`}
                    className={classes.statChip}
                    size="small"
                  />
                  {searchTerm && (
                    <Chip
                      icon={<SearchIcon />}
                      label={`Búsqueda: "${searchTerm}"`}
                      className={classes.statChip}
                      size="small"
                      onDelete={() => setSearchTerm("")}
                    />
                  )}
                  {filterDate && (
                    <Chip
                      icon={<TodayIcon />}
                      label={`Mes: ${
                        filterDate && isValid(filterDate)
                          ? format(filterDate, "MMMM yyyy", { locale: es })
                          : "Fecha inválida"
                      }`}
                      className={classes.statChip}
                      size="small"
                      onDelete={() => setFilterDate("")}
                    />
                  )}
                </Box>
              )}
            </Box>

            {error && (
              <div className={classes.errorContainer}>
                <Alert severity="error">{error}</Alert>
              </div>
            )}

            {loading ? (
              <div className={classes.loadingContainer}>
                <CircularProgress />
                <Typography variant="body1" color="textSecondary">
                  Cargando portafolios...
                </Typography>
              </div>
            ) : filteredPortafolios.length === 0 && portafolios.length === 0 ? (
              <div className={classes.emptyState}>
                <DescriptionIcon className={classes.emptyStateIcon} />
                <Typography variant="h6" gutterBottom>
                  No hay portafolios registrados
                </Typography>
                <Typography variant="body2" color="textSecondary" gutterBottom>
                  Comienza creando tu primer registro de actividades
                </Typography>
                <Button
                  variant="contained"
                  color="primary"
                  className={classes.addButton}
                  onClick={handleAgregarPortafolio}
                  startIcon={<AddIcon />}
                  style={{ marginTop: 16 }}
                >
                  Crear Primer Portafolio
                </Button>
              </div>
            ) : filteredPortafolios.length === 0 ? (
              <div className={classes.emptyState}>
                <SearchIcon className={classes.emptyStateIcon} />
                <Typography variant="h6" gutterBottom>
                  No se encontraron resultados
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Intenta ajustar los filtros de búsqueda
                </Typography>
                <Button
                  variant="outlined"
                  onClick={handleClearFilters}
                  startIcon={<ClearIcon />}
                  style={{ marginTop: 16 }}
                >
                  Limpiar Filtros
                </Button>
              </div>
            ) : (
              <Stack spacing={2}>
                <PortafolioCardList
                  portafolios={filteredPortafolios}
                  cliente={cliente}
                  onEditarPortafolio={handleEditarPortafolio}
                  onDeleteFile={deleteFile}
                  deletingFile={deletingFile}
                  onDeletePortafolio={deletePortafolio}
                  deletingPortafolio={deletingPortafolio}
                  rol={rol}
                />
              </Stack>
            )}
          </CardContent>
        </Card>

        {agregarPortafolio && (
          <AgregarPortafolio
            open={agregarPortafolio}
            onClose={handleCloseModal}
            cliente={cliente}
            portafolio={portafolioSeleccionado}
          />
        )}
      </div>
    </Fade>
  );
}
